import type { Location } from '$lib/serv/locations';
import { EMPTY, Observable, catchError, combineLatest, filter, map, of, switchMap, tap } from "rxjs";
import { locationEqual, migrateCart, setCartInfo, setLSCart, type CartData, getCartLocation, isValidCartLocation } from "./groc-cart";
import type { Store } from '$lib/def/landing';
import { toastError } from './toast';
import { browser } from '$app/environment';

export type Locals = { stores?: Store[], location: Location & { store?: string }};

export type BrowsingStore = {
	/**
	 * The current browsing store.
	 */
	store?: Store,

	/**
	 * Actual (with uuid) or stub cart (without uuid, but valid store and customer).
	 */
	cart: CartData,

	/**
	 * The current location used in deciding the store.
	 */
	location?: Location;

	/**
	 * Redirect required to either deselect or select a store.
	 */
	redirect?: boolean,

	/**
	 * Migration is required to bring a cart to the selected/default store.
	 */
	migrate?: boolean,

	/**
	 * Local storage cart header (cart uuid) needs to be updated.
	 */
	updateLS?: boolean,
};

let debug = true;
export function setDebug(val:boolean) {
	debug = val;
}

/**
 * Create a "browsing" store ID to be used for grocery landing items.
 * @param geolocation$ The browsing location parameters used to get the local stores. A
 * location is only considered valid if it has a longitude and latitude.
 * @param deepLinkStoreId$ The `store` query parameter.
 * @param lsCart$ The current local storage cart.
 * @param userCart$ The logged in user's cart.
 * @returns 
 */
export function createCartAndStoreOutcome(
	localstores$: Observable<Locals>,
	lsCart$: Observable<CartData | null | undefined>,
	userCart$: Observable<CartData | undefined>,
) {
	if (!browser) return EMPTY; // the server should rather not waste time on this
	return combineLatest([
		localstores$,
		lsCart$.pipe(catchError(() => of(null))),
		userCart$.pipe(catchError(() => of(null))),
	]).pipe(
		map(([{ stores:localStores, location }, lsCart, userCart]) => {
			if (!localStores?.length  && !location.store  && !lsCart && !userCart) {
				// if (debug) console.info(`No store info and no cart.`);
				// outcome needs at least one emit to inform downstream of no local stores
				return { cart: {} } as BrowsingStore;
			}

			const deepLinkStoreId = location.store;
			if (debug) console.info(
				`Local Stores: ${localStores?.length ?? '-'} (${localStores?.find(s => s.default)?.id ?? '-'}),`,
				`Geo: ${location.longitude && location.longitude ? 'Yes' : 'No'},`,
				`DL: ${deepLinkStoreId ?? 'No'},`,
				`LS Cart: ${lsCart?.uuid ? `YES (${lsCart.store.id})` : 'No'},`,
				`User Cloud Cart: ${userCart?.uuid ? `YES (${userCart.store.id})` : 'No'}`,
			);

			if (!localStores?.length) {
				if (debug) console.info(`No local stores. Area not currently serviced.`);
				return {
					location, // passthrough
				} as BrowsingStore;
			}

			// get rid of problem: selected/deep link store not found among local stores
			if (deepLinkStoreId && localStores?.length && !localStores.map(s => s.id).includes(deepLinkStoreId)) {
				if (debug) console.info(`Browsing Store: Deep Link Store not local, redirecting...`);
				const store = localStores.find(s => s.default);
				return {
					store,
					location, // passthrough
					redirect: true, // because the url store id is not found among local stores
				} as BrowsingStore;
			}

			// get rid of persisted carts with stores not among local stores
			let lsCartStore = (lsCart && localStores?.length) && localStores.find(s => s.id === lsCart?.store.id) || undefined;
			let userCartStore = (userCart && localStores?.length) && localStores.find(s => s.id === userCart?.store.id) || undefined;
			
			// favour last updated cart (if both exist and are local)
			let persistedCart: CartData | undefined | null = lsCart || userCart;
			if (lsCartStore && userCartStore) {
				// TODO: prompt the customer to choose one of the two carts? Scenario: a user creates a cart then logs in and has a server-side cart.
				// TODO: discuss other factors that may be considered in choosing one of the two carts? (eg. cart address, )
				if ((lsCart?.updated_at_ts ?? 0) > (userCart?.updated_at_ts ?? 0)) {
					persistedCart = lsCart;
					userCartStore = undefined;
					// TODO: set LS cart as user server-side cart (migrate or repeated PUT with auth header?)
					// TODO: at this stage the cart should probably be migrated if the cart location differs from the browsing location. DONE at #reditect_cart_location
				} else {
					persistedCart = userCart;
					lsCartStore = undefined;
				}
				if (lsCart?.uuid !== userCart?.uuid) {
					if (debug) console.info(`Browsing Store: User cloud cart is newer than LS, updating...`);
					// NOTE an LS update will cause all of the above to be re-evaluated
					return {
						store: userCartStore,
						cart: persistedCart,
						updateLS: true,
					} as BrowsingStore;
				}
			}

			const persistedCartStore = persistedCart?.store && localStores?.find(s => s.id === persistedCart?.store.id) || undefined;
			const defaultStore = localStores?.find(s => s.default);
			const deepLinkStore = deepLinkStoreId && localStores?.find(s => s.id === deepLinkStoreId) || undefined;
			const hasBrowsingGeo = typeof location.latitude !== 'undefined' && typeof location.longitude !== 'undefined';

			// if persisted cart...
			if (persistedCart) {
				// find reasons to redirect (either to cart store, cart location or both)
				const browsingStore = deepLinkStore || defaultStore;
				if (
					(!hasBrowsingGeo && persistedCart.customer?.address?.geo_location)
					|| (!browsingStore && persistedCart.store.id)
				) {
					if (debug) console.info(`Browsing Store: Using persisted cart store (after redirecting to cart store AND/OR location).`);
					return {
						store: browsingStore || persistedCartStore,
						location: hasBrowsingGeo ? location : getCartLocation(persistedCart),
						redirect: true, // because there is no locaiton in the url
						cart: persistedCart,
					} as BrowsingStore;
				}
				
				const migrateToStore = !!browsingStore && browsingStore.id !== persistedCart.store.id && browsingStore;
				const migrateToLocation = hasBrowsingGeo && !locationEqual(location, persistedCart.customer.address) && location;
				if (migrateToStore || migrateToLocation) {
					if (debug) console.info(`Browsing Store: Using persisted cart store (with migration to browsing location AND/OR store).`);
					return {
						store: migrateToStore || persistedCartStore,
						location: migrateToLocation || getCartLocation(persistedCart),
						migrate: true,
						cart: persistedCart,
					} as BrowsingStore;
				}

				if (debug) console.info(`Browsing Store: Using persisted cart store.`);
				return {
					store: persistedCartStore,
					cart: persistedCart,
				} as BrowsingStore;
			}

			// all that remain is to use either the deep link or default if either exist
			if (debug) console.info(`Browsing Store: deep link or default local.`);
			const store = deepLinkStore ?? defaultStore;
			const cart = setCartInfo({}, store?.id, location);
			return { store, cart } as BrowsingStore;
		}),
	)
}

/**
 * 
 * @param outcome$ A cart and store outcome observable produced by `createCartAndStoreOutcome`.
 * @param redirect If a store ID is deduced that is different from supplied deep link
 * store a redirect is requested and the observable will not emit.
 * @returns 
 */
export function createCartAndStoreFollowup(
	outcome$: Observable<BrowsingStore>,
	redirect: { next: (params:{store:string, location?:Location})=>void },
) {
	return outcome$.pipe(
		// filter LS updates or redirects which both cause the observable to emit once more
		filter(({ store, location, redirect: doRedirect, updateLS, cart }) => {
			if (updateLS) {
				setLSCart(cart!);
				return false;
			}
			if (doRedirect) {
				redirect.next({store: store?.id ?? '', location });
				return false;
			}
			return true;
		}),
		// pipe migrations
		switchMap(({store, migrate, cart, location }) => {
			if (!migrate) return of({ store, cart });
			return migrateCart(cart!, store?.id ?? '', location!).pipe(
				map(cart => ({ store, cart })),
				catchError(er => {
					/**
					 * While https://mrdelivery.atlassian.net/browse/FEW-1446 is a problem we'll
					 * display a toast and redirect back to the original store.
					 */
					toastError('Store change', false, 'We could not change the store of your cart, possibly due to your item quantity limits.')
					if (store && cart?.store && store.id !== cart.store.id) {
						redirect.next({store: cart?.store?.id ?? '', location }); // change back
					}
					return of({ store: cart.store, cart });
				})
			);
		}),
		// map(({ storeId }) => storeId),
	)
}
